/**
 * Os grupos da tela de Tarefas: Atrasadas · Hoje · Próximos dias (H10).
 *
 * ── Por que POR PRAZO, e não por prioridade ───────────────────────────────
 *
 * A pergunta de quem abre esta tela é "o que eu faço agora", e a resposta é
 * temporal. Uma tarefa urgente para semana que vem não é mais urgente que uma
 * normal para hoje — a prioridade ordena DENTRO do dia, não entre dias.
 *
 * ── Quem decide o atraso é o SERVIDOR ─────────────────────────────────────
 *
 * `atrasada` vem derivada com o expediente da revenda (D01): tarefa vencida às
 * 22h de sexta não conta como atrasada até segunda. Comparar o prazo com o
 * relógio local aqui refaria essa conta errado — e segunda de manhã o vendedor
 * encontraria vinte "atrasadas" que venceram no fim de semana. É a armadilha
 * que a própria task nomeia.
 *
 * Por isso "atrasada" é o campo, e não uma comparação. O relógio local só
 * aparece para separar HOJE de DEPOIS, que é uma pergunta de calendário e não
 * de expediente.
 *
 * ── O que fica de fora dos três ───────────────────────────────────────────
 *
 * Tarefa SEM prazo não entra em nenhum: ela não atrasa, não é de hoje e não é
 * dos próximos dias. Ela vai para "Sem prazo", que é um quarto grupo honesto —
 * enfiá-la em "Próximos dias" diria que há uma data que não existe.
 *
 * Tarefa CONCLUÍDA fica no grupo do prazo dela, esmaecida. Movê-la para um
 * grupo "Feitas" tiraria da vista o alvo do desfazer, que é justamente o que a
 * regra de `shared/ui/tarefa` existe para evitar.
 */

import type { TarefaDaApi } from '@cliqcrm/contracts';

export type ChaveDeGrupo = 'atrasadas' | 'hoje' | 'depois' | 'sem-prazo';

export interface Grupo {
  readonly chave: ChaveDeGrupo;
  readonly nome: string;
  /** O que dizer quando ele está vazio. Cada um é uma notícia diferente. */
  readonly vazio: string;
  readonly itens: readonly TarefaDaApi[];
}

/**
 * A ordem das colunas é a ordem da urgência, da esquerda para a direita.
 *
 * "Sem prazo" fica por último porque é o único que não responde "quando" — e
 * numa tela que organiza por tempo, o que não tem tempo vem depois do que tem.
 */
const ORDEM: readonly {
  chave: ChaveDeGrupo;
  nome: string;
  vazio: string;
}[] = [
  {
    chave: 'atrasadas',
    nome: 'Atrasadas',
    // "Nada atrasado" é BOA notícia, e o texto precisa dizer isso: um vazio
    // genérico faria a coluna parecer quebrada.
    vazio: 'Nada atrasado — é o estado que se quer ver.',
  },
  { chave: 'hoje', nome: 'Hoje', vazio: 'Nada para hoje.' },
  { chave: 'depois', nome: 'Próximos dias', vazio: 'Nada marcado para os próximos dias.' },
  { chave: 'sem-prazo', nome: 'Sem prazo', vazio: 'Toda tarefa tem data marcada.' },
];

/**
 * A qual grupo esta tarefa pertence.
 *
 * `atrasada` primeiro, e não o calendário: uma tarefa que venceu hoje de manhã
 * é atrasada, não "de hoje" — e é na coluna das atrasadas que ela precisa
 * aparecer.
 */
export function grupoDaTarefa(tarefa: TarefaDaApi, agora: Date): ChaveDeGrupo {
  if (tarefa.atrasada) return 'atrasadas';
  if (!tarefa.prazo_em) return 'sem-prazo';

  const prazo = new Date(tarefa.prazo_em);
  if (Number.isNaN(prazo.getTime())) return 'sem-prazo';

  return mesmoDia(prazo, agora) ? 'hoje' : 'depois';
}

/**
 * Agrupa, e ORDENA dentro de cada grupo.
 *
 * Dentro do dia manda a prioridade — é aqui que ela ordena, e é a única coisa
 * que ela ordena. O desempate é o prazo, e depois o título, para a lista não
 * dançar entre dois carregamentos quando duas tarefas empatam em tudo.
 */
export function agruparPorPrazo(
  itens: readonly TarefaDaApi[],
  agora: Date = new Date(),
): readonly Grupo[] {
  const caixas = new Map<ChaveDeGrupo, TarefaDaApi[]>(ORDEM.map(({ chave }) => [chave, []]));

  for (const tarefa of itens) caixas.get(grupoDaTarefa(tarefa, agora))!.push(tarefa);

  return ORDEM.map((grupo) => ({
    ...grupo,
    itens: [...caixas.get(grupo.chave)!].sort(comparar),
  }));
}

/** Do mais urgente para o menos. */
const PESO: Record<string, number> = { urgente: 0, alta: 1, normal: 2, baixa: 3 };

function comparar(a: TarefaDaApi, b: TarefaDaApi): number {
  const porPrioridade = (PESO[a.prioridade] ?? 2) - (PESO[b.prioridade] ?? 2);
  if (porPrioridade !== 0) return porPrioridade;

  // Sem prazo vai para o fim do próprio grupo: dentro de "Sem prazo" todos
  // empatam, e nos outros a ausência não deveria acontecer.
  const pa = a.prazo_em ? Date.parse(a.prazo_em) : Number.POSITIVE_INFINITY;
  const pb = b.prazo_em ? Date.parse(b.prazo_em) : Number.POSITIVE_INFINITY;
  if (pa !== pb) return pa - pb;

  return a.titulo.localeCompare(b.titulo, 'pt-BR');
}

function mesmoDia(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
